/**
 * UserPicker - Reusable modal for selecting users from the system
 * 
 * A standalone component that displays a modal with a searchable list
 * of users for selection with a Promise-based API.
 * 
 * Features:
 * - List of active users from standing data
 * - Search/filter by name
 * - Shows user photo, name, and login
 * - Exclude specific user IDs from results
 * - Single selection mode
 * 
 * Usage:
 * ```typescript
 * import { UserPicker } from '@/components/UserPicker';
 * 
 * // Simple usage
 * const result = await UserPicker.show();
 * if (result.selected) {
 *   console.log('Selected:', result.userId, result.fullName);
 * }
 * 
 * // With options
 * const result = await UserPicker.show({
 *   title: 'Select Owner',
 *   excludeUserIds: [123, 456], // Exclude specific users
 * });
 * ```
 */

import { ModalSystem } from '@/services/ModalSystem';
import { Logger } from '@/services/Logger';
import { trisysAPI } from '@/services/api/TriSysWebAPI';

const logger = Logger.scope('UserPicker');

// ============================================================================
// Types
// ============================================================================

export interface UserPickerOptions {
  /** Modal title (default: 'Select User') */
  title?: string;
  
  /** User IDs to exclude from results */
  excludeUserIds?: number[];
  
  /** Modal width class (default: 'w-[500px] max-w-[95vw]') */
  modalWidth?: string;
  
  /** Whether to include locked users (default: false) */
  includeLocked?: boolean;
}

export interface UserPickerResult {
  /** Whether a user was selected (true) or cancelled (false) */
  selected: boolean;
  
  /** Selected user ID (if selected) */
  userId?: number;
  
  /** Selected user's full name (if selected) */
  fullName?: string;
  
  /** Selected user's login name (if selected) */
  loginName?: string;
  
  /** Selected user's photo URL (if selected) */
  photo?: string;
  
  /** Selected user's contact ID (if selected) */
  contactId?: number;
}

interface StandingDataUser {
  UserId: number;
  FullName: string;
  LoginName: string;
  TelNo?: string;
  ContactId: number;
  Locked: boolean;
  Photo?: string;
}

// ============================================================================
// UserPicker Component
// ============================================================================

/**
 * UserPicker - Static modal for selecting a user
 */
export class UserPicker {
  private options: Required<Omit<UserPickerOptions, 'excludeUserIds'>> & 
    Pick<UserPickerOptions, 'excludeUserIds'>;
  
  private modalId: string | null = null;
  private users: StandingDataUser[] = [];
  private filteredUsers: StandingDataUser[] = [];
  private filterText: string = '';
  
  private resolvePromise: ((result: UserPickerResult) => void) | null = null;
  
  private constructor(options: UserPickerOptions = {}) {
    this.options = {
      title: options.title ?? 'Select User',
      modalWidth: options.modalWidth ?? 'w-[500px] max-w-[95vw]',
      includeLocked: options.includeLocked ?? false,
      excludeUserIds: options.excludeUserIds
    };
  }
  
  /**
   * Show the user picker modal and return a Promise with the result
   * 
   * @param options - Configuration options
   * @returns Promise resolving to the picker result
   * 
   * @example
   * ```typescript
   * const result = await UserPicker.show({
   *   excludeUserIds: [currentOwnerId]
   * });
   * 
   * if (result.selected) {
   *   console.log('Selected user:', result.userId, result.fullName);
   * }
   * ```
   */
  static show(options: UserPickerOptions = {}): Promise<UserPickerResult> {
    const picker = new UserPicker(options);
    return picker.showModal();
  }
  
  private showModal(): Promise<UserPickerResult> {
    return new Promise((resolve) => {
      this.resolvePromise = resolve;
      
      // Load users from standing data
      this.loadUsers();
      
      const modalHtml = this.buildModalHtml();
      
      this.modalId = ModalSystem.show({
        title: this.options.title,
        html: modalHtml,
        width: this.options.modalWidth,
        buttons: [
          {
            caption: 'Cancel',
            className: 'btn-ghost',
            callback: () => {
              this.handleCancel();
              return true;
            }
          }
        ],
        onOpened: () => this.initializeComponents(),
        onClosed: () => this.cleanup()
      });
    });
  }
  
  private loadUsers(): void {
    const standingData = trisysAPI.getCachedStandingData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allUsers: StandingDataUser[] = (standingData as any)?.Users || [];
    
    // Filter users
    const excludeIds = new Set(this.options.excludeUserIds || []);
    this.users = allUsers
      .filter(u => {
        if (excludeIds.has(u.UserId)) return false;
        if (!this.options.includeLocked && u.Locked) return false;
        return true;
      })
      .sort((a, b) => a.FullName.localeCompare(b.FullName));
    
    this.filteredUsers = [...this.users];
    
    logger.debug('Users loaded', { 
      total: allUsers.length, 
      filtered: this.users.length,
      excludeIds: Array.from(excludeIds)
    });
  }
  
  private buildModalHtml(): string {
    return `
      <div class="flex flex-col gap-4">
        <!-- Search box -->
        <div class="form-control">
          <label class="input input-bordered input-sm flex items-center gap-2">
            <i class="fas fa-search text-base-content/50"></i>
            <input type="text" 
                   id="user-picker-search" 
                   class="grow bg-transparent border-none outline-none" 
                   placeholder="Search..."
                   autocomplete="off" />
          </label>
        </div>
        
        <!-- Users list -->
        <div id="user-picker-list" class="max-h-[400px] overflow-y-auto space-y-1">
          ${this.renderUsersList()}
        </div>
        
        ${this.users.length === 0 ? '<p class="text-base-content/50 text-sm text-center">No users available.</p>' : ''}
      </div>
    `;
  }
  
  private renderUsersList(): string {
    if (this.filteredUsers.length === 0) {
      return '<p class="text-base-content/50 text-sm text-center py-4">No users match your search.</p>';
    }
    
    return this.filteredUsers.map(user => {
      const initials = this.getInitials(user.FullName);
      const hasPhoto = user.Photo && user.Photo.trim() !== '';
      
      // Photo avatar or initials fallback
      const avatarContent = hasPhoto
        ? `<img src="${user.Photo}" alt="${this.escapeHtml(user.FullName)}" class="rounded-full w-10 h-10 object-cover" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" /><div class="hidden w-10 h-10 items-center justify-center bg-neutral text-neutral-content rounded-full"><span class="text-base">${initials}</span></div>`
        : `<div class="w-10 h-10 flex items-center justify-center bg-neutral text-neutral-content rounded-full"><span class="text-base">${initials}</span></div>`;
      
      return `
        <div class="flex items-center gap-3 p-2 hover:bg-base-200 rounded-lg cursor-pointer transition-colors user-picker-item"
             data-user-id="${user.UserId}"
             data-full-name="${this.escapeHtml(user.FullName)}"
             data-login-name="${this.escapeHtml(user.LoginName)}"
             data-photo="${user.Photo || ''}"
             data-contact-id="${user.ContactId}">
          <div class="avatar flex-shrink-0">
            ${avatarContent}
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium truncate">${this.escapeHtml(user.FullName)}</div>
            <div class="text-sm text-base-content/60 truncate">${this.escapeHtml(user.LoginName)}</div>
          </div>
          <i class="fas fa-chevron-right text-base-content/30"></i>
        </div>
      `;
    }).join('');
  }
  
  private initializeComponents(): void {
    // Search input
    const searchInput = document.getElementById('user-picker-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        this.filterText = searchInput.value.toLowerCase().trim();
        this.applyFilter();
      });
      // Focus search input
      setTimeout(() => searchInput.focus(), 100);
    }
    
    // User selection
    this.bindUserClickHandlers();
    
    logger.debug('User picker initialized');
  }
  
  private bindUserClickHandlers(): void {
    const listContainer = document.getElementById('user-picker-list');
    if (!listContainer) return;
    
    listContainer.addEventListener('click', (e) => {
      const item = (e.target as HTMLElement).closest('.user-picker-item') as HTMLElement;
      if (item) {
        this.handleUserSelect(item);
      }
    });
  }
  
  private applyFilter(): void {
    if (!this.filterText) {
      this.filteredUsers = [...this.users];
    } else {
      this.filteredUsers = this.users.filter(u => 
        u.FullName.toLowerCase().includes(this.filterText) ||
        u.LoginName.toLowerCase().includes(this.filterText)
      );
    }
    
    const listContainer = document.getElementById('user-picker-list');
    if (listContainer) {
      listContainer.innerHTML = this.renderUsersList();
    }
  }
  
  private handleUserSelect(item: HTMLElement): void {
    const userId = parseInt(item.dataset.userId || '0');
    const fullName = item.dataset.fullName || '';
    const loginName = item.dataset.loginName || '';
    const photo = item.dataset.photo || '';
    const contactId = parseInt(item.dataset.contactId || '0');
    
    logger.debug('User selected', { userId, fullName, loginName });
    
    if (this.resolvePromise) {
      this.resolvePromise({
        selected: true,
        userId,
        fullName,
        loginName,
        photo,
        contactId
      });
    }
    
    if (this.modalId) {
      ModalSystem.hide(this.modalId);
    }
  }
  
  private handleCancel(): void {
    logger.debug('User picker cancelled');
    
    if (this.resolvePromise) {
      this.resolvePromise({ selected: false });
    }
  }
  
  private cleanup(): void {
    this.modalId = null;
    this.resolvePromise = null;
    this.users = [];
    this.filteredUsers = [];
    this.filterText = '';
    
    logger.debug('User picker cleaned up');
  }
  
  private getInitials(name: string): string {
    return name.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');
  }
  
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
